import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "../lib/api";

const RANGES = [
  { id: "7d", label: "7 days" },
  { id: "28d", label: "28 days" },
  { id: "90d", label: "90 days" },
];

const card = "rounded-2xl border border-white/8 bg-[#141113]/70 p-5";

function Badge({ kind }) {
  const map = {
    verified: { t: "Verified", c: "#6FD79E" },
    estimated: { t: "Estimated", c: "#E0B84A" },
  };
  const m = map[kind] || map.verified;
  return (
    <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
      style={{ color: m.c, borderColor: m.c + "66" }} data-testid={`growth-badge-${kind}`}>{m.t}</span>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className={card} data-testid={`growth-stat-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[11px] uppercase tracking-wide text-[#9E978E]">{label}</div>
      <div className="font-serif text-3xl mt-1" style={{ color: accent || "#FBF8F3" }}>{value}</div>
      {sub != null && <div className="text-[11px] text-[#9E978E] mt-1">{sub}</div>}
    </div>
  );
}

function MiniTable({ title, rows, cols }) {
  return (
    <div className={card}>
      <h3 className="text-sm text-[#C9A96E] mb-3">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-[#9E978E]">No data in this period.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody className="divide-y divide-white/6">
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c, j) => (
                  <td key={j} className={`py-2 ${j === 0 ? "text-[#FBF8F3]" : "text-right text-[#C9A96E]"}`}>
                    {c(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function AdminGrowth() {
  const [range, setRange] = useState("28d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const params = (custom.start && custom.end)
        ? { start: custom.start, end: custom.end }
        : { range };
      const res = await api.get("/growth/admin-metrics", { params });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "Not authorized." : "Failed to load growth metrics.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  if (loading && !data) return <div className="text-[#9E978E]" data-testid="growth-loading">Loading growth metrics…</div>;
  if (err) return <div className="text-[#E58CA0]" data-testid="growth-error">{err}</div>;
  if (!data) return null;

  const t = data.traffic || {};
  const f = data.funnel || {};
  const en = data.engagement || {};
  const attr = data.attribution || {};
  const ref = data.referral || {};

  return (
    <div className="space-y-8" data-testid="admin-growth">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button key={r.id} data-testid={`growth-range-${r.id}`}
              onClick={() => { setCustom({ start: "", end: "" }); setRange(r.id); }}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${range === r.id && !(custom.start && custom.end) ? "bg-[#541224] text-[#FBF8F3]" : "border border-white/10 text-[#9E978E] hover:text-[#FBF8F3]"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input type="date" data-testid="growth-custom-start" value={custom.start}
            onChange={(e) => setCustom({ ...custom, start: e.target.value })}
            className="bg-[#0C0A0A] border border-white/10 rounded-lg px-2 py-1 text-xs text-[#FBF8F3]" />
          <span className="text-[#9E978E] text-xs">to</span>
          <input type="date" data-testid="growth-custom-end" value={custom.end}
            onChange={(e) => setCustom({ ...custom, end: e.target.value })}
            className="bg-[#0C0A0A] border border-white/10 rounded-lg px-2 py-1 text-xs text-[#FBF8F3]" />
          <button data-testid="growth-custom-apply" onClick={load} disabled={!custom.start || !custom.end}
            className="px-3 py-1.5 rounded-full text-xs bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] disabled:opacity-40">Apply</button>
        </div>
        <div className="ml-auto text-[11px] text-[#9E978E]" data-testid="growth-range-meta">
          {data.range?.start?.slice(0, 10)} → {data.range?.end?.slice(0, 10)} · Times in {data.timezone}
        </div>
      </div>

      {/* TRAFFIC */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-serif text-xl text-[#FBF8F3]">Website Traffic</h2>
          <Badge kind="verified" />
        </div>
        {!t.collector_active ? (
          <div className={card} data-testid="growth-traffic-unavailable">
            <div className="text-[#E0B84A] text-sm font-medium">Traffic collector active — no visits recorded yet</div>
            <div className="text-xs text-[#9E978E] mt-1">
              First-party page-view logging is live. Numbers appear here as visitors browse the public site.
              This is a genuine "no data yet" state, not a zero.
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {t.total_pageviews === 0 && (
              <div className={card} data-testid="growth-traffic-empty-range">
                <div className="text-[#E0B84A] text-sm font-medium">No visits recorded in this period</div>
                <div className="text-xs text-[#9E978E] mt-1">
                  The page-view collector is active and has data in other periods — try a wider range. This is a genuine zero for the selected dates, not missing configuration.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Page Views" value={t.total_pageviews} accent="#D4AF37" />
              <Stat label="Unique Visitors" value={t.unique_visitors} />
              <Stat label="Sessions" value={t.sessions} />
              <Stat label="New / Returning" value={`${t.new_visitors} / ${t.returning_visitors}`} accent="#8FB4FF" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={card}>
                <h3 className="text-sm text-[#C9A96E] mb-4">Visitors by Day</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={(t.timeseries || []).map((d) => ({ name: d.date.slice(5), value: d.visitors }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2326" />
                    <XAxis dataKey="name" tick={{ fill: "#9E978E", fontSize: 9 }} />
                    <YAxis tick={{ fill: "#9E978E", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#1C171A", border: "1px solid #D4AF37", borderRadius: 8 }} />
                    <Line type="monotone" dataKey="value" stroke="#D4AF37" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className={card}>
                <h3 className="text-sm text-[#C9A96E] mb-4">Honolulu Guide Traffic</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={(t.article_views || []).map((a) => ({ name: a.path.replace("/", "").replace(/-/g, " ").slice(0, 16), value: a.views }))}>
                    <XAxis dataKey="name" tick={{ fill: "#9E978E", fontSize: 9 }} angle={-15} textAnchor="end" height={50} interval={0} />
                    <YAxis tick={{ fill: "#9E978E", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#1C171A", border: "1px solid #D4AF37", borderRadius: 8 }} />
                    <Bar dataKey="value" fill="#6FD79E" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
              <MiniTable title="Top Sources" rows={t.by_source} cols={[(r) => r.name, (r) => r.value]} />
              <MiniTable title="Referrers" rows={t.by_referrer} cols={[(r) => r.name, (r) => r.value]} />
              <MiniTable title="Landing Pages" rows={t.landing_pages} cols={[(r) => r.name, (r) => r.value]} />
              <MiniTable title="Devices" rows={t.by_device} cols={[(r) => r.name, (r) => r.value]} />
            </div>
          </div>
        )}
      </section>

      {/* REGISTRATION FUNNEL */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-serif text-xl text-[#FBF8F3]">Registration Funnel</h2>
          <Badge kind="verified" />
        </div>
        <p className="text-[11px] text-[#9E978E] mb-3">
          Cohort = members who registered in this period, followed through each milestone (computed from current state).
          Hover a milestone for its exact definition.
        </p>
        <div className={card} data-testid="growth-funnel-milestones">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wide text-[#9E978E] text-left">
              <th className="py-2">Milestone</th><th className="text-right">Members</th><th className="text-right">Of registrations</th>
            </tr></thead>
            <tbody className="divide-y divide-white/6">
              {(f.milestones || []).map((m) => (
                <tr key={m.key} title={m.definition} data-testid={`growth-milestone-${m.key}`}>
                  <td className="py-2 text-[#FBF8F3]">{m.label}
                    <span className="block text-[10px] text-[#9E978E] font-normal max-w-xl">{m.definition}</span>
                  </td>
                  <td className="text-right font-serif text-xl"
                    style={{ color: m.key === "approved" || m.key === "registration" ? "#6FD79E" : "#FBF8F3" }}>{m.count}</td>
                  <td className="text-right text-[#C9A96E]">{m.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <Stat label="Reg. Page Visitors" value={t.collector_active ? t.registration_page_visitors : "—"}
            sub="unique /auth visitors" />
          <Stat label="Registration Starts" value={t.collector_active ? t.register_starts : "—"} sub="form opened (estimated)" />
        </div>
      </section>

      {/* ENGAGEMENT */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-serif text-xl text-[#FBF8F3]">Member Engagement</h2>
          <Badge kind="verified" />
        </div>
        <p className="text-[11px] text-[#9E978E] mb-3">Confirmed backend outcomes within the selected period.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Offers Submitted" value={en.offers_submitted} accent="#D4AF37" />
          <Stat label="First-Offer Creators" value={en.first_offer_creators} />
          <Stat label="Offers Accepted" value={en.offers_accepted} accent="#6FD79E" />
          <Stat label="Connections Created" value={en.connections_created} accent="#6FD79E" />
          <Stat label="First Interactions" value={en.first_interactions} accent="#8FB4FF" />
        </div>
      </section>

      {/* ATTRIBUTION */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-serif text-xl text-[#FBF8F3]">Marketing Attribution</h2>
          <Badge kind="verified" />
        </div>
        <p className="text-[11px] text-[#9E978E] mb-3">
          {attr.method}. Attributed: {attr.attributed_registrations} · Unattributed (direct/organic): {attr.unattributed_registrations}.
        </p>
        <div className={card} data-testid="growth-attribution-table">
          {(!attr.rows || attr.rows.length === 0) ? (
            <div className="text-xs text-[#9E978E]">No campaign-tagged registrations in this period. Tag your links with
              <span className="text-[#C9A96E]"> ?utm_source=…&utm_campaign=…</span> to see attribution here.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wide text-[#9E978E] text-left">
                <th className="py-2">Campaign</th><th>Source</th>
                <th className="text-right">Registrations</th><th className="text-right">Approved</th><th className="text-right">First Offer</th>
              </tr></thead>
              <tbody className="divide-y divide-white/6">
                {attr.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2 text-[#FBF8F3]">{r.campaign}</td>
                    <td className="text-[#C9A96E]">{r.source || "—"}</td>
                    <td className="text-right text-[#C9A96E]">{r.registrations}</td>
                    <td className="text-right text-[#6FD79E]">{r.approved}</td>
                    <td className="text-right text-[#8FB4FF]">{r.first_offer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* REFERRAL */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-serif text-xl text-[#FBF8F3]">Referral Tracking</h2>
          <Badge kind="verified" />
        </div>
        <p className="text-[11px] text-[#9E978E] mb-3">Capture-only — referral source recorded at signup via <span className="text-[#C9A96E]">?ref=…</span>. No credits or rewards are issued.</p>
        <MiniTable
          title="Referred Registrations"
          rows={ref.rows}
          cols={[(r) => r.source, (r) => `${r.registrations} reg`, (r) => `${r.approved} appr`, (r) => `${r.first_offer} offer`]}
        />
      </section>
    </div>
  );
}
